import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, ExternalLink, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

/** The register of licensed Kenyan professionals, and the honest list of what we never do ourselves. */
export default function Professionals() {
  const [d, setD] = useState(null);
  const [category, setCategory] = useState("");

  useSeo({
    title: "The Verified Professional Network | KickStartKenyaFootball",
    description:
      "Regulated work in Kenya belongs with licensed professionals — EARB estate agents, registered valuers, advocates, surveyors, planners, BORAQS architects and quantity surveyors, EBK engineers, NCA contractors, NEMA experts and CMA-licensed intermediaries. Verification means we checked the regulator's own register.",
  });

  useEffect(() => {
    api.get("/professionals", { params: category ? { category } : {} })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [category]);

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="professionals-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="professionals-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Independent licensed professionals</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.heading}</h1>
          <p className="body-copy mt-6 max-w-3xl">{d.intro}</p>
        </Reveal>

        <div className="mt-10 border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="professionals-position">
          <p className="text-sm leading-relaxed text-zinc-300">
            Everyone listed here is independent of KickStartKenyaFootball. We authorise, accredit, approve,
            licence and appoint nobody, we do not forward your submission to any of them, and we are paid
            nothing either way &mdash; you make the call yourself.
          </p>
          <details className="mt-4" data-testid="professionals-position-details">
            <summary className="mono cursor-pointer text-[0.58rem] uppercase tracking-[0.16em] text-ochre">
              Read the full position
            </summary>
            <div className="mt-4 space-y-3 text-xs leading-relaxed text-zinc-400">
              <p data-testid="professionals-independence">{d.independence}</p>
              <p data-testid="professionals-verification-meaning">{d.verification_meaning}</p>
              {d.lawful_disclosure ? <p data-testid="professionals-lawful-disclosure">{d.lawful_disclosure}</p> : null}
            </div>
          </details>
        </div>

        <section className="mt-20" data-testid="professionals-categories">
          <p className="eyebrow">The map of regulated work</p>
          <h2 className="display-3 mt-4">Who Holds Each Question, And Who Regulates Them</h2>
          <p className="body-copy mt-5 max-w-3xl">
            Every line below is work KickStartKenyaFootball does not do and cannot lawfully do. It is here so
            nobody has to guess who to ask, or take our word for whether someone may act.
          </p>
          <div className="mt-8 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left" data-testid="professionals-categories-table">
              <thead>
                <tr className="border-b border-white/15 bg-[#0b0b0b]">
                  {["Who you need", "For what", "Their regulator", "What they must hold", "Listed"].map((h) => (
                    <th key={h} className="mono px-4 py-3 text-[0.52rem] uppercase tracking-[0.16em] text-zinc-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.categories.map((c) => (
                  <tr key={c.id} className="border-b border-white/5" data-testid={`professional-category-${c.id}`}>
                    <td className="px-4 py-4 text-sm text-white">{c.label}</td>
                    <td className="px-4 py-4 text-xs text-zinc-400">{c.for}</td>
                    <td className="px-4 py-4 text-xs text-zinc-300">
                      {c.register_url ? (
                        <a href={c.register_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-ochre hover:underline">
                          {c.regulator} <ExternalLink size={11} />
                        </a>
                      ) : c.regulator}
                    </td>
                    <td className="px-4 py-4 text-xs text-zinc-400">{c.needs}</td>
                    <td className="mono px-4 py-4 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
                      {d.by_category?.[c.id] || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-20" data-testid="professionals-register">
          <p className="eyebrow">The register</p>
          <h2 className="display-3 mt-4">Checked Against Their Own Regulator</h2>
          <Link to="/professionals/join"
            className="mono mt-5 inline-block text-[0.6rem] uppercase tracking-[0.16em] text-ochre underline underline-offset-4"
            data-testid="professionals-join-link">
            Hold a current Kenyan licence? Ask to be listed — we check your number, then we publish it
          </Link>

          <div className="mt-8 flex flex-wrap gap-2" data-testid="professionals-filter">
            <button type="button" onClick={() => setCategory("")}
              data-testid="professionals-filter-all"
              className={`mono border px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] transition-colors ${
                category ? "border-zinc-700 text-zinc-400 hover:border-ochre hover:text-ochre" : "border-ochre text-ochre"
              }`}>
              Everyone
            </button>
            {d.categories.map((c) => (
              <button key={c.id} type="button" onClick={() => setCategory(c.id)}
                data-testid={`professionals-filter-${c.id}`}
                className={`mono border px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] transition-colors ${
                  category === c.id ? "border-ochre text-ochre" : "border-zinc-700 text-zinc-400 hover:border-ochre hover:text-ochre"
                }`}>
                {c.label}
              </button>
            ))}
          </div>

          {d.rows.length ? (
            <div className="mt-8 grid gap-px bg-zinc-800 md:grid-cols-2 lg:grid-cols-3" data-testid="professionals-list">
              {d.rows.map((p) => (
                <article key={p.id} className="bg-[#0b0b0b] p-6" data-testid={`professional-${p.id}`}>
                  <p className="mono inline-flex items-center gap-1.5 text-[0.55rem] uppercase tracking-[0.16em] text-ochre">
                    <BadgeCheck size={12} /> Independently verified {p.verified_on}
                  </p>
                  <h3 className="mt-3 font-display text-lg uppercase tracking-tight text-white">{p.legal_name}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{p.category_label}</p>
                  <dl className="mt-5 space-y-2 text-xs text-zinc-400">
                    <div><dt className="inline text-zinc-600">Regulator: </dt><dd className="inline">{p.regulator}</dd></div>
                    <div><dt className="inline text-zinc-600">Registration: </dt><dd className="inline">{p.registration_number || "—"}</dd></div>
                    {p.practising_certificate ? (
                      <div><dt className="inline text-zinc-600">Practising certificate: </dt><dd className="inline">{p.practising_certificate}</dd></div>
                    ) : null}
                    {p.counties?.length ? (
                      <div><dt className="inline text-zinc-600">Counties served: </dt><dd className="inline">{p.counties.join(", ")}</dd></div>
                    ) : null}
                    {p.indemnity ? (
                      <div><dt className="inline text-zinc-600">Professional indemnity: </dt><dd className="inline">{p.indemnity}</dd></div>
                    ) : null}
                  </dl>
                  {p.register_url ? (
                    <a href={p.register_url} target="_blank" rel="noreferrer"
                      data-testid={`professional-register-${p.id}`}
                      className="mono mt-5 inline-flex items-center gap-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-ochre hover:underline">
                      Check the register yourself <ExternalLink size={11} />
                    </a>
                  ) : null}
                  <p className="mono mt-4 text-[0.5rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-600">
                    Independent of KickStartKenyaFootball
                  </p>
                </article>
              ))}
            </div>
          ) : (
            <p className="mono mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-maasai bg-[#0b0b0b] p-6 text-[0.6rem] uppercase leading-relaxed tracking-[0.12em] text-zinc-300"
              data-testid="professionals-empty">
              <ShieldAlert size={16} className="mt-0.5 shrink-0 text-maasai" />
              {d.empty_note}
            </p>
          )}
        </section>

        <div className="mt-20 flex flex-wrap gap-3" data-testid="professionals-related">
          <Link to="/professionals/join" className="btn-primary" data-testid="professionals-join-cta">
            Licensed? Ask to be listed
          </Link>
          <Link to="/what-if" className="btn-ghost">What if Kenyan football had this?</Link>
          <Link to="/apply" className="btn-ghost">Tell us about an opportunity</Link>
          <Link to="/contact" className="btn-ghost">Ask us something else</Link>
        </div>
      </div>
    </div>
  );
}
